<template>
    <!--
      Teleport to body so `position: fixed` resolves against the viewport instead of the
      route-component ancestor, which has a `motion-blur-in-*` class (filter creates a
      containing block). Without this the modal lands inside the animated route, which on
      mobile manifests as a half-rendered/"frozen" page (HAP-1068).
    -->
    <Teleport to="body" :disabled="small">
    <div :class="{'fixed w-full h-screen inset-0 bg-gray-800/50 dark:bg-black/60 backdrop-blur-[2px] flex items-center justify-center z-[10000] top-0 overscroll-none cursor-default py-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]': !small}" @click="$emit('hide')">
        <slot
            :logout="logout"
            :login="login"
            :createAccount="createAccount"
            :sendRecoveryEmail="sendRecoveryEmail"
            :resetPassword="resetPassword"
        >
            <form @submit.stop.prevent :class="{
                'p-8 dark:bg-gray-700 bg-white border dark:border-gray-600 shadow-card w-full max-w-md mx-4': !small,
                'bg-transparent': small,
                'pointer-events-none': processing,
                'opacity-50': small && processing
            }" class="rounded-lg flex flex-col space-y-4 min-w-0" @click.stop>

                <div v-if="label" class="text-2xl">{{ labelText }}</div>

                <!--
                  HAP-4134: one email can map to 2+ member records (e.g. two family members
                  sharing an address). The server verifies the password against each and,
                  when more than one matches, returns `candidates` instead of a session so
                  the user can say who they are. Picking re-submits the same credentials with
                  the chosen memberId, which the server re-verifies before minting.
                -->
                <div v-if="candidates && candidates.length" class="flex flex-col space-y-3">
                    <p class="text-sm text-gray-600 dark:text-gray-300">This email is used by more than one account. Which one is you?</p>
                    <button v-for="c in candidates" :key="c.memberId" type="button" :disabled="processing" @click="chooseCandidate(c.memberId)" class="px-3 py-2 rounded border text-left dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
                        {{ candidateName(c) }}
                    </button>
                    <button type="button" @click="candidates = null" class="text-xs text-gray-400 hover:underline">Use a different email</button>
                </div>

                <template v-else>
                <div v-if="creatingAccount && !awaitingOtp" class="flex gap-4 w-full">
                    <input class="border rounded px-3 py-2 w-full dark:bg-transparent dark:border-gray-500" type="text" autocomplete="given-name" v-model.trim="firstName" placeholder="First Name" ref="firstname" />
                    <input class="border rounded px-3 py-2 w-full dark:bg-transparent dark:border-gray-500" type="text" autocomplete="family-name" v-model.trim="lastName" placeholder="Last Name" />
                </div>

                <input v-if="!resettingPassword && !awaitingOtp" class="border rounded px-3 py-2 w-full dark:bg-transparent dark:border-gray-500" type="email" v-model.trim="email" placeholder="Email" />

                <input v-show="!guest && !forgotPassword && !awaitingOtp" @keydown.enter="action" class="border rounded px-3 py-2 w-full dark:bg-transparent dark:border-gray-500" type="password" v-model.trim="password" placeholder="Password" :autocomplete="creatingAccount ? 'new-password' : 'current-password'" />

                <!-- OTP Input -->
                <div v-if="awaitingOtp" class="flex flex-col gap-2 min-w-0">
                    <p v-if="existingAccountOtp" class="text-sm text-gray-600 dark:text-gray-300">You already have an account with <b class="break-all">{{ email }}</b>. Enter the one-time code we emailed to log in — your registration continues right where you left off.</p>
                    <p v-else class="text-sm text-gray-600 dark:text-gray-300">Enter the code sent to <span class="break-all">{{ email }}</span></p>
                    <input @keydown.enter="verifyOtp" class="border rounded px-3 py-2 w-full dark:bg-transparent dark:border-gray-500 text-center tracking-widest" type="text" v-model.trim="otp" placeholder="Enter Code" maxlength="6" />
                </div>

                <button @click="awaitingOtp ? verifyOtp() : action()" class="px-3 py-2 dark:text-white/75 text-white rounded flex justify-center w-full" :class="buttonColors">
                    <template v-if="!processing">{{ buttonText }}</template>
                    <Spinner v-else class="border-t-family-at-church-orange w-4 h-4 my-1" :small="true" color="border-t-white/50" />
                </button>

                <button v-if="store.user?.member?.id" @click="logout" class="text-xs text-gray-400 hover:underline">Logout</button>

                <button v-if="allowGuest && !guest" @click="activateGuest" class="text-ncfic-blue-400 text-lg font-semibold underline dark:border-gray-500">Continue as Guest</button>
                <div class="text-center select-none" @click="$router.push({query: null})">
                    <button v-if="!creatingAccount"     @click="creatingAccount = true; guest = false; forgotPassword = false" class="text-xs text-gray-400 hover:underline">Create Account</button>
                    <button v-if="creatingAccount || resettingPassword || awaitingOtp"      @click="creatingAccount = false; guest = false; forgotPassword = false; awaitingOtp = false; existingAccountOtp = false; otp = null" class="text-xs text-gray-400 hover:underline" :class="{'ml-2 pl-2 border-l dark:border-gray-500': resettingPassword || awaitingOtp}">{{ resettingPassword || awaitingOtp || (allowGuest && !guest) ? 'Login' : 'Have an account? Login instead.' }}</button>
                    <button v-if="!creatingAccount && !resettingPassword"     @click="creatingAccount = false; guest = false; forgotPassword = true" class="text-xs text-gray-400 hover:underline ml-2 pl-2 border-l dark:border-gray-500">Forgot Password?</button>
                </div>
                </template>
            </form>
        </slot>
    </div>
    </Teleport>
</template>

<script>
import Swal from '../../helpers/swal' // lazy sweetalert2 (HAP-4635)
import { validateEmail, subscribeToMailChimp } from '../../helpers/email'
import { getUser } from '../../helpers/user'
import { clearClientSession } from '../../helpers/authSession'
import Mango from '../../helpers/mango';
import dayjs from 'dayjs'
import { fireFormSuccess, fireFormError } from '../../helpers/leadConversion.js'

// Function for setting cookies
let setCookie = function (cname, cvalue) {
    var d = new Date();
    d.setTime(d.getTime() + (365 * 24 * 60 * 60 * 1000));
    var expires = "expires=" + d.toUTCString();
    document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
}

export default {
    props: {
        small: {type: Boolean, default: false},
        label: {type: Boolean, default: true},
        allowGuest: {type: Boolean, default: true},
        defaultGuest: {type: Boolean, default: false},
        leadGen: {type: Boolean, default: false},
        buttonColors: {type: String, default: 'bg-family-at-church-orange dark:bg-family-at-church-orange/80'},
        source: {type: String, default: 'account'},
    },
    inject: ['store','axios'],
    data() {
        return {
            email: this.$route?.query?.email || null,
            password: null,
            firstName: null,
            lastName: null,
            processing: false,
            creatingAccount: false,
            guest: false,
            forgotPassword: false,
            awaitingOtp: false,
            // HAP-4236: a guest who enters an email that already has an account is
            // sent a one-time code and logged into that account. That is intentional
            // (a guest must prove ownership before we attach a purchase), but it must
            // not feel like a silent hijack — this flag drives copy that says so, and
            // the in-progress cart survives because beginRegistration re-creates it
            // for THIS event after login (see helpers/registration.js completed-cart guard).
            existingAccountOtp: false,
            otp: null,
            // HAP-4134: shared-email disambiguation. `candidates` holds the member
            // records that matched the submitted password; `chosenMemberId` is the
            // one the user picked, consumed one-shot by login().
            candidates: null,
            chosenMemberId: null,
        }
    },
    mounted() {
        // iOS-safe scroll lock: position:fixed preserves scroll, whereas `overflow:hidden h-screen`
        // collapses the body to 100vh and lets iOS Safari snap the scroll back to 0, which the user
        // reads as the page freezing (HAP-1068).
        if (!this.small) {
            const scrollY = window.scrollY
            document.body.dataset.scrollLockY = String(scrollY)
            document.body.style.position = 'fixed'
            document.body.style.top = `-${scrollY}px`
            document.body.style.left = '0'
            document.body.style.right = '0'
            document.body.style.width = '100%'
        }
        if (this.defaultGuest || this.leadGen) this.activateGuest()
    },
    unmounted() {
        if (!this.small) {
            const scrollY = parseInt(document.body.dataset.scrollLockY || '0', 10)
            document.body.style.position = ''
            document.body.style.top = ''
            document.body.style.left = ''
            document.body.style.right = ''
            document.body.style.width = ''
            delete document.body.dataset.scrollLockY
            window.scrollTo(0, scrollY)
        }
    },
    watch: {
        // If getUser() hydrates a session after the modal opened (empty store →
        // member id), close the modal AND continue the pending action. Emitting
        // only `hide` left Register's loginAction uncalled, so the user was
        // "logged in" with no cart — attendees then locked the form.
        // Skip `loggedIn` while `processing` so login() is the single emitter
        // on an intentional submit (otherwise continueRegistration would run twice).
        loggedIn(isLoggedIn) {
            if (!isLoggedIn) return
            this.$nextTick(() => {
                // login() already emitted loggedIn for an intentional submit.
                if (!this.processing && !this._handledLogin) this.$emit('loggedIn')
                this.$emit('hide')
            })
        }
    },
    methods: {
        activateGuest() { this.creatingAccount = true; this.guest = true; this.forgotPassword = false },
        validateEmail,
        async login() {
            this.processing = true
            // HAP-4134: the disambiguation picker sets chosenMemberId; consume it one-shot
            // so a stale pick can't leak into a later, unrelated attempt.
            const chosenMemberId = this.chosenMemberId
            this.chosenMemberId = null
            const payload = { email: this.email, password: (this.password || this.guestPassword) }
            if (chosenMemberId) payload.memberId = chosenMemberId
            let member = (await this.axios.post(`${this.store.api}/controllers/account/login`, payload))?.data

            // HAP-4134: this email maps to 2+ member records that share this password.
            // The server minted no session; ask the user which person they are.
            if (member?.needsDisambiguation) {
                this.candidates = member.candidates || []
                this.processing = false
                return
            }
            this.candidates = null

            if (member?.memberId) {
                // A session was actually established. createAccount(), verifyOtp() and
                // resetPassword() all funnel through here, so `account_login` legitimately
                // fires alongside `account_create` / `password_reset` on those flows.
                fireFormSuccess({ formId: 'account_login' })
                if (member.roles?.includes('admin')) member.admin = true

                window.localStorage.setItem('user', member.memberId)
                window.localStorage.setItem('email', this.email)
                window.localStorage.setItem('auth', `${member.token}:${member.memberId}`)
                setCookie(`Authorization`, `${member.token}:${member.memberId}`)

                // window.__insp.push(['identify', this.email])

                this.axios.defaults.headers.common['Authorization'] = `${member.token}:${member.memberId}`

                const user = await getUser()
                this._handledLogin = true
                this.store.user = user
                if (this.store.cart?.length) this.store.saveCart()
                else if (this.store.hydrateFromServer) this.store.hydrateFromServer()

                // HAP-4886: a function `next` is invoked here, once. Clear it before emitting —
                // nav's @loggedIn handler also runs function nexts, and both firing made the
                // Add Church signup create every church twice.
                const pendingNext = this.store?.login?.next
                if (typeof pendingNext === 'function') this.store.login.next = null

                this.$emit('loggedIn')
                // OTP / recovery copy lives on the login form. A leftover Swal
                // (same text, OK button) would sit on top of attendees after verify.
                Swal.close()

                // Registration wizard routes are protected and store themselves
                // in login.next. Pushing there immediately races beginRegistration
                // and lands the user on attendees with no cart (locked inputs).
                // The @loggedIn handler (continueRegistration / selectRate)
                // navigates after the cart exists. Functions are invoked by nav.
                let restrictedPath = typeof pendingNext === 'function' ? pendingNext : this.store?.login?.next
                const isWizard = typeof restrictedPath === 'string' && /\/events\/[^/]+\/registration(\/|$)/.test(restrictedPath)
                if (typeof restrictedPath === 'function') restrictedPath()
                else if (restrictedPath && !isWizard) this.$router.push(restrictedPath)
                if (this.store.login) this.store.login.next = null

                this.$emit('hide')

                if (this.store.median?.onesignal) {
                    let conferences = await Mango.registrations({ search: { authorId: this.store.user?.member?.id }, fields: ['rate'], depthLimit: 2 })
                    let tags = {
                        userId: this.store.user?.member?.id,
                        email: this.store.user?.member?.email,
                        firstName: this.store.user?.member?.firstName,
                        lastName: this.store.user?.member?.lastName,
                    }
                    for (let c of conferences) { tags[`${dayjs(c.rate.event.startDate).format('YYYY')}-${c.rate.event.shortName?.toLowerCase()?.trim()?.replace(/[^a-zA-Z0-9\s]/g, '')?.replace(/\s/g, '-')}`] = 'true' }

                    let result = await this.store.median.onesignal.tags.setTags({tags})
                    try {
                        if (!result?.success) Swal.fire({ title: 'Tagging Error', text: `${result}, ${result?.message}, ${JSON.stringify(tags)}`, icon: 'warning' })
                    } catch (e) {
                        if (this.store.user?.member?.roles?.includes('admin')) {
                            Swal.fire({ title: 'Median Error', text: `${e} --- ${e.message}`, icon: 'error' })
                        }
                    }
                }

            } else {
                fireFormError({ formId: 'account_login' })
                if (member?.invalidFields?.length) Swal.fire({ title: `Invalid ${member.invalidFields.join(', ')}`, icon: 'error' })
            }

            this.processing = false
        },
        chooseCandidate(memberId) {
            // HAP-4134: user picked which shared-email account to sign in as. Re-run
            // login() with the same credentials plus the chosen memberId; the server
            // re-verifies the password against that member before minting a session.
            this.chosenMemberId = memberId
            this.login()
        },
        candidateName(c) {
            return [c.firstName, c.lastName].filter(Boolean).join(' ').trim() || 'This account'
        },
        async logout() {
            // HAP-3628: actually revoke the session token server-side, not just
            // forget it locally -- best-effort, since a network failure here
            // shouldn't block the user from logging out on this device (the
            // token still expires on its own, and can be revoked in bulk later).
            try {
                await this.axios.post(`${this.store.api}/controllers/account/logout`)
            } catch (e) {
                console.log(e)
            }
            clearClientSession()
            this.store.user = {}
            // window.location.reload()
            this.$emit('loggedOut')
            this.$emit('hide')
        },
		async createAccount() {
			if (!this.validateEmail(this.email)) return Swal.fire('Email must be a valid email.')
			if (this.password?.length < 6 && !this.guest) return Swal.fire('Password must be at least 6 characters.')
			this.processing = true

            // Guest checkout is "create a member with a password they never see."
            // If this email already has an account, skip that POST (it 400s with a
            // random password in the payload) and OTP into the existing member.
            if (this.guest && !this.leadGen) {
                this.existingAccountOtp = true
                if (await this.sendOtpForExistingAccount({ quietIfMissing: true })) {
                    this.processing = false
                    return
                }
                this.existingAccountOtp = false
            }

			var data = {
				email: this.email,
				password: this.password || this.guestPassword,
				firstName: this.firstName,
				lastName: this.lastName,
                title: `${this.firstName} ${this.lastName}`,
                source: this.source,
			}

            let response = null
            try {
                response = await this.axios.post(`${this.store.api}/members`, data)
                if (response?.data?.success) {
                    fireFormSuccess({ formId: 'account_create' })
                    // subscribeToMailChimp({email: this.email, list: 'master', source: 'account', alert: false})
                    await this.login()
                    this.$emit('accountCreated')
                    this.$emit('hide')
                } else {
                    // Mango answers 200 with { success:false } on rejection, so this branch is
                    // reachable without an exception. UX is unchanged (it stays silent today).
                    fireFormError({ formId: 'account_create' })
                }
            } catch (e) {
                fireFormError({ formId: 'account_create' })
                if (this.leadGen) {
                    this.$emit('accountExists', true)
                } else if (this.guest) {
                    this.existingAccountOtp = true
                    await this.sendOtpForExistingAccount()
                } else {
                    Swal.fire('Error', e, 'warning')
                }
                console.log(e)
                // Swal.fire('Error', e, 'error')
            }
            this.processing = false
        },
        async sendOtpForExistingAccount({ quietIfMissing = false } = {}) {
            try {
                let response = await this.axios.post(`${this.store.api}/controllers/account/sendResetInstructions`, { email: this.email })
                if (response.data.success) {
                    this.awaitingOtp = true
                    return true
                }
                if (!quietIfMissing) Swal.fire('Error', 'Could not send verification code. Please try again.', 'error')
            } catch (e) {
                console.log(e)
                if (!quietIfMissing) Swal.fire('Error', e.message, 'error')
            }
            return false
        },
        async verifyOtp() {
            if (!this.otp || this.otp.length < 4) return Swal.fire('Please enter the code sent to your email.')
            this.processing = true
            this.password = this.otp
            await this.login()
            this.processing = false
        },
		async sendRecoveryEmail () {
			this.processing = true
			let response = await this.axios.post(`${this.store.api}/controllers/account/sendResetInstructions`, {email: this.email, forgot: true})
            if (response.data.success) {
                fireFormSuccess({ formId: 'password_recovery_request' })
                this.forgotPassword = false
                this.awaitingOtp = true
                this.otp = null
                Swal.fire('Success!', `Recovery instructions sent to: ${this.email}. Enter the code from your email to sign in.`, 'success')
                this.$emit('emailSent')
            } else {
                fireFormError({ formId: 'password_recovery_request' })
                Swal.fire('Invalid Email!', `The following user does not exist: ${this.email}`, 'warning')
                this.$emit('hide')
            }
            this.processing = false
		},
		async resetPassword () {
            if (this.password.length < 6) return Swal.fire('Password must be at least 6 characters.')
			this.processing = true

            let data = {
                email: this.$route.query.email,
                salt: this.$route.query.salt,
                password: this.password
            }

			let response = await this.axios.post(`${this.store.api}/controllers/account/resetPassword`, data)
            if (response.data.success) {
                fireFormSuccess({ formId: 'password_reset' })
                this.email = this.$route.query.email
                await this.login()
                this.$router.push({query: null})
                Swal.fire('Success!', 'Your password has been reset.', 'success')
            } else {
                fireFormError({ formId: 'password_reset' })
                Swal.fire('Invalid Link!', `The link you're using is invalid.`, 'error')
            }

            this.processing = false
            this.$emit('passwordReset')
            this.$emit('hide')
		},
    },
    computed: {
        guestPassword() { return Math.random().toString(36).substr(2)+Math.random().toString(36).substr(2) },
        labelText() { return this.candidates?.length ? 'Choose Account' : this.awaitingOtp ? 'Verify Code' : this.resettingPassword ? 'Reset Password' : this.forgotPassword ? 'Recover Password' : this.creatingAccount ? this.guest ? 'Continue as Guest' : 'Create Account' : 'Login' },
        buttonText() { return this.awaitingOtp ? 'Verify' : this.resettingPassword ? 'Update Password' : this.forgotPassword ? 'Send Recovery Email' : this.creatingAccount ? this.guest ? 'Continue' : 'Create' : 'Login' },
        action() { return this.resettingPassword ? this.resetPassword : this.forgotPassword ? this.sendRecoveryEmail : this.creatingAccount ? this.createAccount : this.login },
        resettingPassword() { return !!this.$route.query?.salt },
        loggedIn() { return !!this.store.user?.member?.id },
    }
}
</script>
